import React from "react";
import { inputStyles } from "../shared/FormStyles";
import { toast } from "sonner";
import { navigatetoHome } from '@/utils/footer-navigations'

// Add the same CSS animation for consistency
const expandAnimation = {
  enter: "transition-all duration-300 ease-out",
  enterFrom: "transform scale-95 opacity-0",
  enterTo: "transform scale-100 opacity-100",
  leave: "transition-all duration-200 ease-in",
  leaveFrom: "transform scale-100 opacity-100",
  leaveTo: "transform scale-95 opacity-0"
};

const RewardTypeCard = ({
  type,
  title,
  description,
  icon,
  selected,
  onClick,
  disabled,
  requiredPlan
}) => (
  <div
    onClick={onClick}
    className={`
       rounded-lg border-2 transition-all relative
      ${selected ? "border-blue-500 bg-blue-50" : "border-gray-200"}
      ${disabled ? 'cursor-default p-10' : 'hover:border-blue-200 cursor-pointer p-6'}
    `}
  >
    <div className="flex items-start gap-4">
      <div className="text-2xl">{icon}</div>
      <div>
        <h3 className="font-semibold text-lg">{title}</h3>
        <p className="text-gray-600 text-sm mt-1">{description}</p>
      </div>
    </div>
    
    {disabled && (
      <div
        className="absolute inset-0 rounded-lg"
        style={{
          background:
            'linear-gradient(180deg, rgba(255,255,255,0.9) 0%, rgba(249,250,251,0.95) 100%)',
          backdropFilter: 'blur(2px)',
        }}
      >
        <div className="flex h-full flex-col items-center justify-center p-4 text-center">
          <span className="text-2xl mb-2">🔒</span>
          <h3 className="font-semibold text-lg">{title}</h3>
          <p className="text-sm font-medium text-gray-900 ">Available in {requiredPlan} Plan</p>
          <button
            className=" px-4 py-1.5 bg-blue-500 text-white text-sm font-medium rounded-full 
                       hover:bg-blue-600 transition-colors"
            onClick={(e) => {
              e.stopPropagation()
              navigatetoHome('/#prices')
            }}
          >
            Upgrade Now
          </button>
        </div>
      </div>
    )}
  </div>
);

const RewardConfigStep = ({ formData, onUpdate, onBack, onNext, plan }) => {
  const [expandedType, setExpandedType] = React.useState(formData?.rewardType || null);

  const rewardTypes = [
    {
      id: "points",
      title: "Points Reward",
      description: "Reward customers with reward points in their loyalty card",
      icon: "🎯",
      available: ['premium', 'enterprise'].includes(plan?.toLowerCase()), // Available for all plans
      requiredPlan: "Premium",
    },
    {
      id: "discount",
      title: "Percentage Discount",
      description: "Offer a percentage discount on their next purchase",
      icon: "💰",
      available: true, // Available for all plans
      requiredPlan: "Basic",
    },
    {
      id: "free-item",
      title: "Free Item",
      description: "Reward with a free product or service",
      icon: "🎁",
      // Restrict free-item to Premium and Enterprise plans
      available: true,
      requiredPlan: "Basic",
    },
  ];

  const getRewardQtyLabel = (type) => {
    switch (type) {
      case 'points': return 'Number of points to reward';
      case 'discount': return 'Discount percentage (%)';
      case 'free-item': return 'Number of free items';
      default: return 'Reward quantity';
    }
  };

  const getInputMessage = () => {
    if (!formData.rewardQty || formData.rewardQty <= 0) {
      return formData.rewardType === 'discount' 
        ? 'Enter the discount percentage to proceed'
        : formData.rewardType === 'points'
        ? 'Enter the number of points to reward'
        : 'Enter the number of free items to reward (max 5)';
    }
    return '✓ Ready to proceed';
  };

  const getMaxValue = (type) => {
    switch (type) {
      case 'discount': return 100;
      case 'free-item': return 5;
      default: return undefined;
    }
  };

  const getMaxValueMessage = (type) => {
    switch (type) {
      case 'discount': return 'Maximum discount is 100%';
      case 'free-item': return 'Maximum 5 free items allowed';
      default: return '';
    }
  };

  const handleRewardTypeSelect = (type) => {
    const rewardType = rewardTypes.find(t => t.id === type);
    if (!rewardType.available) return; // Don't proceed if not available for plan
    
    setExpandedType(type === expandedType ? null : type);
    onUpdate({
      ...formData,
      rewardType: type,
      rewardQty: 0
    });
  };

  const handleQuantityChange = (value, type) => {
    const maxValue = getMaxValue(type);
    const parsedValue = parseInt(value, 10);

    if (maxValue && parsedValue > maxValue) {
      toast.error(getMaxValueMessage(type));
      return;
    }

    onUpdate({
      ...formData,
      rewardQty: parsedValue,
    });
  };

  // Keep type expanded when returning
  React.useEffect(() => {
    if (formData?.rewardType && !expandedType) {
      setExpandedType(formData.rewardType);
    }
  }, []);

  return (
    <div className="space-y-6">
      {/* Updated header section with flex layout */}
      <div className="flex items-center justify-between mb-8 w-full">
        <button
          onClick={onBack}
          className="px-6 py-2 bg-blue-500 text-white rounded-full hover:bg-blue-600 
                   transition-colors flex items-center gap-2 text-sm font-medium shadow-sm"
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
          Back
        </button>
        <h2 className="text-2xl font-bold self-center">Set Reward</h2>
      </div>

      <p className="text-gray-600 text-center -mt-4 mb-8">Choose what customers will receive</p>

      <div className="space-y-4">
        {rewardTypes.map((type) => (
          <div 
            key={type.id}
            className={`
              border rounded-xl overflow-hidden transition-all duration-300
              ${formData.rewardType === type.id ? 'shadow-lg border-blue-500' : 'shadow-sm hover:shadow-md'}
            `}
          >
            <RewardTypeCard
              {...type}
              selected={formData.rewardType === type.id}
              disabled={!type.available}
              onClick={() => handleRewardTypeSelect(type.id)}
            />

            {expandedType === type.id && (
              <div className={`p-4 bg-gradient-to-b from-blue-50/50 to-white border-t border-blue-100 ${expandAnimation.enter}`}>
                <div className="space-y-4">
                  <div className="w-full max-w-xs mx-auto">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      {getRewardQtyLabel(type.id)}
                    </label>
                    <div className="relative">
                      <input
                        type="number"
                        min="1"
                        max={getMaxValue(type.id)}
                        className={`
                          ${inputStyles.base}
                          text-center text-lg font-medium
                          focus:ring-2 focus:ring-blue-500 focus:border-blue-500
                        `}
                        value={formData.rewardQty || ''}
                        onChange={(e) => handleQuantityChange(e.target.value, type.id)}
                      />
                      
                      {/* Help text */}
                      <div className="mt-1 text-center">
                        <p className="text-sm text-gray-500">
                          {type.id === 'discount' 
                            ? '💰 Percentage off their next purchase'
                            : type.id === 'points'
                            ? '🎯 Points added to their loyalty card'
                            : '🎁 Number of items they get for free'}
                        </p>
                      </div>

                      {/* Next button right after input */}
                      <div className="pt-6 flex flex-col items-center gap-2">
                        <button
                          onClick={onNext}
                          disabled={!formData.rewardQty || formData.rewardQty <= 0}
                          className="
                            px-8 py-3 bg-blue-500 text-white rounded-full
                            hover:bg-blue-600 hover:scale-105 transform
                            transition-all duration-200 flex items-center gap-2
                            text-sm font-medium shadow-md
                            disabled:bg-blue-300 disabled:hover:scale-100 
                            disabled:hover:bg-blue-300 disabled:cursor-not-allowed
                          "
                        >
                          Continue to {formData.rewardStyle === 'stamps' ? 'Stamp' : 'Progress Bar'} Design
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                          </svg>
                        </button>
                        
                        <p className={`text-sm ${formData.rewardQty > 0 ? 'text-green-500' : 'text-gray-500'}`}>
                          {getInputMessage()}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};

export default RewardConfigStep;